import React, { useEffect, useState } from "react";
import type { Timestamp } from "firebase/firestore";
import type { Item } from "../../App";
import { FunnelIcon as HeroFunnelIcon } from "@heroicons/react/24/outline";
import { FilterPopover, AnchorRect } from "../../components/FilterPopover";
import { fmtDateOnly } from "../../lib/formatters";
import { fmtUpdateTime, formatSerialForDisplay } from "../../lib/inventoryHelpers";

type RowState = "open" | "ready" | "invoiced";

type Filters = {
  orderNumber: string;
  manufacturer: string;
  model: string;
  customer: string;
  serial: string;
  chargerIncluded: string;
  createdAt: string;
  createdBy: string;
};

type FilterKey = keyof Filters;

type InventoryItem = Item;

const CrossedFilterIcon = (): JSX.Element => (
  <span className="relative inline-flex h-6 w-6 text-danger">
    <HeroFunnelIcon className="h-6 w-6 opacity-70" />
    <span className="absolute inset-0 flex items-center justify-center">
      <svg
        className="h-6 w-6 text-danger"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M5 19 19 5" />
      </svg>
    </span>
  </span>
);

const normalizeCreatedAt = (val: unknown): string | Date | Timestamp | null | undefined => {
  if (val === null || val === undefined) return val;
  if (val instanceof Date) return val;
  if (typeof val === "string") return val;
  if (typeof val === "number") return new Date(val);
  if (typeof val === "object" && "seconds" in (val as Record<string, unknown>)) {
    return val as Timestamp;
  }
  return undefined;
};

interface Props {
  isLoadingItems: boolean;
  isMobile: boolean;
  items: InventoryItem[];
  visibleItems: InventoryItem[];
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
  filtersActive: boolean;
  clearAllFilters: () => void;
  openFilterKey: string | null;
  setOpenFilterKey: React.Dispatch<React.SetStateAction<string | null>>;
  filterSearch: string;
  setFilterSearch: (val: string) => void;
  loadingFilterOptions: boolean;
  filterOptions: Record<string, string[]>;
  distinctValues: Record<string, string[]>;
  filterAnchor: AnchorRect | null;
  handleOpenFilter: (key: string, e: React.MouseEvent<HTMLButtonElement>) => void;
  pendingDeletableIds: string[];
  openDeleteModal: () => void;
  getDeleteToggleMeta: (
    item: InventoryItem,
    state: RowState,
    lockedByOther: boolean,
    lockHint: string
  ) => { disabled: boolean; title: string };
  onToggleDeleteFromList: (item: InventoryItem, checked: boolean) => void;
  deleteTogglePendingIds?: Set<string>;
  customerNameById: Record<string, string>;
  currentUserString: () => string | null;
  hasNewTopItems: boolean;
  setHasNewTopItems: (val: boolean) => void;
  fetchFirstPage: () => void;
  fetchNextPage: () => void;
  pageIndex: number;
  pageIsLoading: boolean;
  pageHasNext: boolean;
  pageLastRefreshAt: number | null;
  loadMoreRef?: React.MutableRefObject<HTMLDivElement | null>;
  isCustomerPortal: boolean;
  lastError: string | null;
  FunnelIcon: React.FC<{ size?: number }>;
  openEdit: (item: InventoryItem) => void;
  showTableFilters?: boolean;
  tableWrapClassName?: string;
}

const ClearIcon: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10ZM9.5 9.5l5 5M14.5 9.5l-5 5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

export function InventoryListView({
  isLoadingItems,
  isMobile,
  items,
  visibleItems,
  filters,
  setFilters,
  filtersActive,
  clearAllFilters,
  openFilterKey,
  setOpenFilterKey,
  filterSearch,
  setFilterSearch,
  loadingFilterOptions,
  filterOptions,
  distinctValues,
  filterAnchor,
  handleOpenFilter,
  pendingDeletableIds,
  openDeleteModal,
  getDeleteToggleMeta,
  onToggleDeleteFromList,
  deleteTogglePendingIds,
  customerNameById,
  currentUserString,
  hasNewTopItems,
  setHasNewTopItems,
  fetchFirstPage,
  fetchNextPage,
  pageIndex,
  pageIsLoading,
  pageHasNext,
  pageLastRefreshAt,
  loadMoreRef,
  isCustomerPortal,
  lastError,
  FunnelIcon,
  openEdit,
  showTableFilters = true,
  tableWrapClassName = "",
}: Props): JSX.Element {
  const [showBackToTop, setShowBackToTop] = useState(false);
  const deleteToggleBusy = !!deleteTogglePendingIds?.size;

  useEffect(() => {
    const onScroll = () => setShowBackToTop(window.scrollY > 500);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const optionsForKey = (key: string | null): string[] => {
    if (!key) return [];
    return distinctValues[key] ?? filterOptions[key] ?? [];
  };

  const filterPopover = (
    <FilterPopover
      openKey={openFilterKey}
      anchor={filterAnchor}
      loading={loadingFilterOptions}
      search={filterSearch}
      onSearchChange={setFilterSearch}
      options={optionsForKey(openFilterKey)}
      onSelect={(v) => {
        if (!openFilterKey) return;
        setFilters((f) => ({ ...f, [openFilterKey]: v } as Filters));
        setOpenFilterKey(null);
      }}
      onClear={() => {
        if (!openFilterKey) return;
        setFilters((f) => ({ ...f, [openFilterKey]: "" } as Filters));
        setOpenFilterKey(null);
      }}
      onClose={() => setOpenFilterKey(null)}
    />
  );

  const header = <div className="gw-section-header flex items-center h-12 mb-3 w-full max-w-[1500px] mx-auto" />;

  const FilterControls: React.FC<{ filterKey: FilterKey; label: string }> = ({ filterKey, label }) => {
    const isActive = !!filters[filterKey];
    return (
      <div className="flex items-center gap-1 justify-start">
        <span className="align-middle">{label}</span>
        <div className="flex items-center gap-0">
          <button
            className={`gw-btn-icon ${isActive ? "text-primary" : ""}`}
            onClick={(e) => handleOpenFilter(filterKey, e)}
            aria-label={`Filtrera ${label}`}
          >
            <FunnelIcon size={18} />
          </button>
          {isActive && (
            <button
              className="gw-btn-icon text-red-500 animate-pulse -ml-3"
              onClick={() => setFilters((f) => ({ ...f, [filterKey]: "" } as Filters))}
              aria-label={`Rensa filter för ${label}`}
            >
              <ClearIcon size={20} />
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={showTableFilters ? "w-full max-w-[1800px] mx-auto pr-4 md:pr-0 pl-16 md:pl-20" : "w-full h-full"}>
      <section className={showTableFilters ? "mt-3" : ""}>
        {showTableFilters && header}
          {hasNewTopItems && pageIndex > 1 && (
            <div
              role="status"
              aria-live="polite"
              className="flex items-center justify-between gap-3 p-2.5 my-2.5 rounded-md bg-yellow-50 border border-yellow-200 text-sm"
            >
              <span className="text-yellow-800">Det finns nya poster överst i listan.</span>
              <button
                className="gw-btn gw-btn-secondary gw-btn-sm"
                onClick={() => { setHasNewTopItems(false); fetchFirstPage(); }}
              >
                Ladda nya
              </button>
            </div>
          )}

          {/* MOBIL */}
          {isMobile ? (
            <>
              {filtersActive && visibleItems.length === 0 && !pageIsLoading && (
                <div className="p-2 mb-2 text-muted">
                  <div className="font-semibold mb-1">0 träffar för valda filter</div>
                  <button className="gw-btn gw-btn-secondary" onClick={clearAllFilters}>Rensa alla filter</button>
                </div>
              )}

              <div className="grid gap-3">
                {visibleItems.map((it) => {
                  const state: RowState = it.invoiceReportId ? "invoiced" : it.completed ? "ready" : "open";
                  const lockedByOther = !!it.lockedBy && it.lockedBy !== currentUserString();
                  const lockAt = it.lockedAt as Date | string | number | null | undefined;
                  const lockHint = lockedByOther
                    ? `Redigeras av ${it.lockedBy}${lockAt ? " sedan " + new Date(lockAt).toLocaleString() : ""}`
                    : "";

                  const { disabled: checkboxDisabledBase, title: deleteTitleBase } = getDeleteToggleMeta(
                    it,
                    state,
                    lockedByOther,
                    lockHint
                  );
                  const checkboxDisabled = checkboxDisabledBase || deleteToggleBusy;
                  const deleteTitle = deleteToggleBusy
                    ? "Vänta tills föregående markering är klar"
                    : deleteTitleBase;

                  return (
                    <div key={it.id} className={`p-3 bg-surface border border-border rounded-lg shadow-sm row-${state}`}>
                      <div className="flex items-center justify-between mb-2">
                        <label className="inline-flex items-center gap-2">
                          <input

                            type="checkbox"

                            checked={!!it.deletePending}

                            onChange={(e) => { if (!checkboxDisabled) onToggleDeleteFromList(it, e.target.checked); }}

                            disabled={checkboxDisabled}

                            title={deleteTitle}

                            className={checkboxDisabled ? "opacity-60 cursor-not-allowed" : ""}
                          />
                          <span className="font-semibold">
                            {it.manufacturer || "-"} {it.model || ""}
                          </span>
                        </label>
                      </div>

                      <div className="grid grid-cols-[100px_1fr] gap-x-2 gap-y-1.5 text-sm">
                        <span className="text-muted">Ordernr</span>
                        <span>{it.orderNumber || "-"}</span>

                        <span className="text-muted">Kund</span>
                        <span>{it.customer || customerNameById[it.customerId || ""] || "-"}</span>

                        <span className="text-muted">Serienr</span>
                        <span>
                          {it.serial ? (
                            <button onClick={() => openEdit(it)} className="text-primary hover:underline font-medium" title="Visa/Redigera">
                              {formatSerialForDisplay(it.serial)}
                            </button>
                          ) : "—"}
                        </span>

                        <span className="text-muted">Adapter</span>
                        <span>{it.chargerIncluded ? "Ja" : "Nej"}</span>

                        <span className="text-muted">Skapad</span>
                        <span>{fmtDateOnly(normalizeCreatedAt(it.createdAt))}</span>

                        <span className="text-muted">Skapad av</span>
                        <span>{it.createdBy || (it.initials as string | undefined) || "-"}</span>
                      </div>
                    </div>
                  );
                })}
              </div>

            </>

          ) : (
          /* DESKTOP */
          <div className={`gw-table-wrap relative ${tableWrapClassName}`}>
            {filtersActive && showTableFilters && (
              <button
                type="button"
                onClick={clearAllFilters}
                className="gw-btn gw-btn-icon text-danger animate-pulse-icon-danger absolute right-2 top-3 rounded-xl p-1.5 bg-danger/10 hover:bg-danger/20"
                title="Rensa alla filter"
                aria-label="Rensa alla filter"
              >
                <CrossedFilterIcon />
              </button>
            )}
            <table>
              <thead>
                <tr>
                  {!isCustomerPortal && (
                    <th className="w-16 px-0">
                      <div className="flex justify-center items-center h-full">
                        {pendingDeletableIds.length > 0 && (
                          <button
                            type="button"
                            onClick={openDeleteModal}
                            disabled={!!deleteTogglePendingIds && deleteTogglePendingIds.size > 0}
                            className="gw-btn gw-btn-icon rounded-xl p-1.5 bg-danger/10 hover:bg-danger/20 animate-pulse-icon-danger"
                            title="Radera markerade"
                            aria-label="Radera markerade"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              fill="none"
                              width="28"
                              height="28"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden="true"
                            >
                              <path d="M9.5 4.5h5a.5.5 0 0 1 .5.5v1.5h-6V5a.5.5 0 0 1 .5-.5Z" />
                              <path d="M4.75 6.5h14.5" />
                              <path d="M7 6.5v11A1.5 1.5 0 0 0 8.5 19h7A1.5 1.5 0 0 0 17 17.5v-11" />
                              <path d="M10 10.5v6M14 10.5v6" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </th>
                  )}
                  {showTableFilters ? (
                    <>
                      <th className="w-[120px]"><FilterControls filterKey="orderNumber" label="Ordernr" /></th>
                      <th className="w-[170px]"><FilterControls filterKey="customer" label="Kund" /></th>
                      <th className="w-[140px]"><FilterControls filterKey="manufacturer" label="Tillverkare" /></th>
                      <th className="w-[220px]"><FilterControls filterKey="model" label="Modell" /></th>
                      <th className="w-[180px]"><FilterControls filterKey="serial" label="Serienr" /></th>
                      <th className="w-[120px] text-center">
                        <div className="flex justify-center">
                          <FilterControls filterKey="chargerIncluded" label="Adapter" />
                        </div>
                      </th>
                      <th className="w-[120px] text-center">
                        <div className="flex justify-center">
                          <FilterControls filterKey="createdAt" label="Skapad" />
                        </div>
                      </th>
                      <th className="w-[200px]"><FilterControls filterKey="createdBy" label="Skapad av" /></th>
                    </>
                  ) : (
                    <>
                      <th className="w-[120px] text-left">Ordernr</th>
                      <th className="w-[170px] text-left">Kund</th>
                      <th className="w-[140px] text-left">Tillverkare</th>
                      <th className="w-[220px] text-left">Modell</th>
                      <th className="w-[180px] text-left">Serienr</th>
                      <th className="w-[120px] text-center">Adapter</th>
                      <th className="w-[120px] text-center">Skapad</th>
                      <th className="w-[200px] text-left">Skapad av</th>
                    </>
                  )}
                </tr>
              </thead>

              <tbody>
                {filtersActive && visibleItems.length === 0 && !pageIsLoading && (
                  <tr>
                    <td colSpan={isCustomerPortal ? 8 : 9} className="p-4 text-center">
                      <div className="font-semibold mb-1">0 träffar för valda filter</div>
                      <button className="gw-btn gw-btn-secondary" onClick={clearAllFilters}>Rensa alla filter</button>
                    </td>
                  </tr>
                )}
                {((pageIsLoading && filtersActive) || lastError) && (
                  <tr>
                    <td colSpan={isCustomerPortal ? 8 : 9} className="p-4">
                      {pageIsLoading && filtersActive && (
                        <div className="text-sm text-muted mb-1">
                          Hämtar filtrerade resultat…
                        </div>
                      )}
                      {lastError && (
                        <div className="text-sm text-red-700">
                          Kunde inte ladda listan: {lastError}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
                {visibleItems.map((it) => {
                  const state: RowState = it.invoiceReportId ? "invoiced" : it.completed ? "ready" : "open";
                  const lockedByOther = !!it.lockedBy && it.lockedBy !== currentUserString();
                  const lockAt = it.lockedAt as Date | string | number | null | undefined;
                  const lockHint = lockedByOther
                    ? `Redigeras av ${it.lockedBy}${lockAt ? " sedan " + new Date(lockAt).toLocaleString() : ""}`
                    : "";

                  const { disabled: checkboxDisabledBase, title: deleteTitleBase } = getDeleteToggleMeta(
                    it,
                    state,
                    lockedByOther,
                    lockHint
                  );
                  const checkboxDisabled = checkboxDisabledBase || deleteToggleBusy;
                  const deleteTitle = deleteToggleBusy
                    ? "Vänta tills föregående markering är klar"
                    : deleteTitleBase;

                  return (
                    <tr key={it.id} className={`row-${state}`}>
                      {!isCustomerPortal && (
                        <td className="w-16 px-0">
                                              <div className="flex justify-center items-center h-full">
                                                <input
                                                  type="checkbox"
                                                  checked={!!it.deletePending}
                                                  onChange={(e) => onToggleDeleteFromList(it, e.target.checked)}
                                                  disabled={checkboxDisabled}
                                                  title={deleteTitle}
                                                  className={checkboxDisabled ? "opacity-60 cursor-not-allowed" : ""}
                                                />
                                              </div>
                                            </td>                      )}

                      <td className="whitespace-nowrap w-[120px]">{it.orderNumber || "-"}</td>
                      <td className="whitespace-nowrap min-w-[170px]">
                        {it.customer || customerNameById[it.customerId || ""] || "-"}
                      </td>
                      <td className="w-[140px] max-w-[140px] truncate" title={it.manufacturer || ""}>
                        {it.manufacturer || "-"}
                      </td>
                      <td className="whitespace-nowrap max-w-[220px] truncate" title={it.model || ""}>
                        {it.model || "-"}
                      </td>

                      <td className="w-[180px] max-w-[180px] truncate">
                        {it.serial ? (
                          <button
                            onClick={() => openEdit(it)}
                            className="text-primary hover:underline font-medium block truncate text-left"
                            title="Visa/Redigera"
                          >
                            <span className="truncate">{formatSerialForDisplay(it.serial)}</span>
                          </button>
                        ) : (
                          "—"
                        )}
                      </td>

                      <td className="text-center whitespace-nowrap w-[120px]">{it.chargerIncluded ? "Ja" : "Nej"}</td>
                      <td className="text-center whitespace-nowrap w-[120px]">
                        {fmtDateOnly(normalizeCreatedAt(it.createdAt))}
                      </td>
                      <td className="whitespace-normal">
                        <div className="flex gap-1.5 items-center">
                          <span>{it.createdBy || (it.initials as string | undefined) || "-"}</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className={`grid items-center gap-3 mt-2 ${isMobile ? 'grid-cols-1' : 'grid-cols-[1fr_auto_1fr] w-full max-w-[1500px] mx-auto'}`}>
          <span className="text-muted text-sm">
            Sida {pageIndex} · Uppdateras automatiskt — senast {fmtUpdateTime(pageLastRefreshAt)}
            {pageIsLoading ? " (laddar…)" : ""}
          </span>

          {pageHasNext ? (
            <button
              className="gw-btn gw-btn-secondary"
              onClick={fetchNextPage}
              disabled={pageIsLoading}
              aria-busy={pageIsLoading}
              title="Visa fler"
              style={{ justifySelf: isMobile ? "start" : "center" }}
            >
              {pageIsLoading ? "Laddar…" : "Visa fler"}
            </button>
          ) : (
            <span className="text-xs text-muted" style={{ justifySelf: isMobile ? "start" : "center" }}>
              {items.length > 0 ? "Inga fler poster." : "—"}
            </span>
          )}

          {showBackToTop && (
            <button
              type="button"
              className="gw-btn gw-btn-secondary"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              title="Till toppen"
              style={{ justifySelf: isMobile ? "start" : "end" }}
            >
              Till toppen
            </button>
          )}
        </div>

        {pageHasNext && (
          <div ref={loadMoreRef ?? undefined} aria-hidden="true" className="h-1 w-full" />
        )}

        {pageIsLoading && pageHasNext && (
          <div role="status" aria-live="polite" className="flex justify-center mt-2">
            <span className="text-xs text-muted">Laddar fler…</span>
          </div>
        )}

        {filterPopover}
      </section>

      <div className="mt-3 text-xs text-muted">
        {isLoadingItems && "Laddar enheter…"}
      </div>
    </div>
  );
}
